import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import FieldRenderer from "@/components/FieldRenderer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, CheckCircle2, MessageCircleHeart, Compass, Send, Share2, Copy,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const passShowIf = (q, answers) => {
  const c = q.showIf;
  if (!c) return true;
  const v = answers[c.q];
  if (c.op === "gte") return typeof v === "number" && v >= c.value;
  if (c.op === "lte") return typeof v === "number" && v <= c.value;
  if (c.op === "in") return Array.isArray(c.value) && c.value.includes(v);
  if (c.op === "eq") return v === c.value;
  return true;
};

const SECTION_ICON = { character: MessageCircleHeart, direction: Compass };

export default function Intervju({ shared = false, token }) {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [summaries, setSummaries] = useState({});
  const [si, setSi] = useState(0);
  const [qi, setQi] = useState(0);
  const [phase, setPhase] = useState("q"); // "q" | "summary"
  const [genLoading, setGenLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferAllLoading, setTransferAllLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [sharing, setSharing] = useState(false);
  const timer = useRef(null);

  const putAnswers = (ans) => shared
    ? api.put(`/interview/shared/${token}`, { answers: ans })
    : api.put("/interview", { answers: ans });

  useEffect(() => {
    if (shared) {
      api.get(`/interview/shared/${token}`).then((r) => {
        setSchema(r.data.schema);
        setAnswers(r.data.answers || {});
        setSummaries(r.data.summaries || {});
      }).catch(() => setSchema([]));
    } else {
      Promise.all([api.get("/interview/schema"), api.get("/interview")]).then(([s, sess]) => {
        setSchema(s.data);
        setAnswers(sess.data?.answers || {});
        setSummaries(sess.data?.summaries || {});
      });
    }
  }, [shared, token]);

  const saveAnswers = (next) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { putAnswers(next).catch(() => {}); }, 700);
  };

  const setAnswer = (id, val) => {
    const next = { ...answers, [id]: val };
    setAnswers(next);
    saveAnswers(next);
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/interview/share-link");
      setShareLink(data.link);
      setShareOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  if (!schema) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const section = schema[si];
  const visible = section.questions.filter((q) => passShowIf(q, answers));
  const q = visible[qi];
  const totalSections = schema.length;

  const flush = () => { if (timer.current) clearTimeout(timer.current); putAnswers(answers).catch(() => {}); };

  const next = () => {
    flush();
    if (phase === "q") {
      if (qi < visible.length - 1) setQi(qi + 1);
      else setPhase("summary");
    } else {
      if (si < totalSections - 1) { setSi(si + 1); setQi(0); setPhase("q"); }
    }
  };

  const back = () => {
    if (phase === "summary") { setPhase("q"); setQi(Math.max(0, visible.length - 1)); return; }
    if (qi > 0) { setQi(qi - 1); return; }
    if (si > 0) {
      const prev = schema[si - 1];
      const prevVisible = prev.questions.filter((qq) => passShowIf(qq, answers));
      setSi(si - 1); setPhase("summary"); setQi(Math.max(0, prevVisible.length - 1));
    }
  };

  const genSummary = async () => {
    setGenLoading(true);
    try {
      const url = shared ? `/interview/shared/${token}/summary` : "/interview/summary";
      const { data } = await api.post(url, { section_id: section.id });
      setSummaries((p) => ({ ...p, [section.id]: data.summary }));
      toast.success("Sammanfattning skapad utifrån dina svar");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  const transfer = async () => {
    setTransferLoading(true);
    try {
      await api.post("/interview/transfer", { section_id: section.id });
      toast.success("Sammanfattningen är överförd till din profil");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTransferLoading(false); }
  };

  const transferAll = async () => {
    setTransferAllLoading(true);
    try {
      const { data } = await api.post("/interview/transfer-all");
      const n = (data.transferred || []).length;
      if ((data.missing || []).length) {
        toast.success(`${n} avsnitt överförda. Saknar sammanfattning: ${data.missing.join(", ")}`);
      } else {
        toast.success("Hela intervjun är överförd till din profil");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTransferAllLoading(false); }
  };

  const isLastSection = si === totalSections - 1;
  const summary = summaries[section.id];
  const doneCount = Object.values(summaries).filter((s) => s && s.text).length;
  const progress = phase === "summary" ? 100 : Math.round(((qi + 1) / Math.max(1, visible.length)) * 100);
  const SIcon = SECTION_ICON[section.id] || Sparkles;

  return (
    <div>
      {!shared && (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <PageHeader
            title="Guidad intervju"
            description="Ett lugnt flöde med en fråga i taget om dina mjuka sidor. Svaren sparas separat som en intervju – du väljer själv om du vill föra över sammanfattningen till din profil."
          />
          <Button variant="outline" className="rounded-full mt-1 shrink-0" onClick={createShareLink} disabled={sharing} data-testid="interview-share-btn">
            {sharing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />} Fortsätt i mobilen
          </Button>
        </div>
      )}
      {shared && (
        <div className="text-center mb-6">
          <h1 className="font-head text-2xl font-bold">Guidad intervju</h1>
          <p className="text-sm text-muted-foreground mt-1">Fortsätt i din egen takt – dina svar sparas automatiskt.</p>
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Fortsätt intervjun i mobilen</DialogTitle>
            <DialogDescription>Öppna länken på din telefon för att fortsätta där du slutade. Länken är personlig och gäller i 7 dagar – dela den inte med andra.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={shareLink} data-testid="share-link-input" />
            <Button className="rounded-full shrink-0" onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Länk kopierad"); }} data-testid="share-copy-btn">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><SIcon className="h-4 w-4 text-primary" /> {section.title}</span>
          <span data-testid="interview-progress-label">Avsnitt {si + 1} av {totalSections}</span>
        </div>
        <Progress value={progress} className="h-2 mb-6" />

        {phase === "q" && q && (
          <Card className="rounded-2xl border-border p-6 md:p-8 stagger-in" data-testid="interview-question-card" key={`${section.id}-${q.id}`}>
            <div className="text-xs text-muted-foreground mb-2">Fråga {qi + 1} av {visible.length}</div>
            <h3 className="font-head text-xl font-semibold mb-5" data-testid="interview-prompt">{q.prompt}</h3>
            <FieldRenderer
              field={{ name: q.id, type: q.type, options: q.options, help: q.help }}
              value={answers[q.id]}
              onChange={(v) => setAnswer(q.id, v)}
            />
            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" className="rounded-full" onClick={back} disabled={si === 0 && qi === 0} data-testid="interview-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka
              </Button>
              <Button className="rounded-full" onClick={next} data-testid="interview-next-btn">
                Nästa <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {phase === "summary" && (
          <Card className="rounded-2xl border-primary/25 bg-primary/[0.03] p-6 md:p-8 stagger-in" data-testid="interview-summary-card">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
              <h3 className="font-head text-lg font-semibold">Avsnittet klart: {section.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Skapa en varm sammanfattning av dina svar. Den bygger enbart på det du själv skrivit.</p>

            {summary?.text ? (
              <div className="space-y-4" data-testid="interview-summary-content">
                <div className="rounded-xl bg-white border border-border p-4">
                  <p className="text-sm leading-relaxed whitespace-pre-line">{summary.text}</p>
                </div>
                {(summary.highlights || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {summary.highlights.map((h, i) => <Badge key={i} variant="secondary" className="rounded-full">{h}</Badge>)}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button variant="outline" className="rounded-full" onClick={genSummary} disabled={genLoading} data-testid="interview-regenerate-btn">
                    {genLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Skapa om
                  </Button>
                  {!shared && (
                    <Button className="rounded-full" onClick={transfer} disabled={transferLoading} data-testid="interview-transfer-btn">
                      {transferLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} För över till min profil
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                  <ShieldCheck className="h-4 w-4 text-primary" /> Intervjun hålls separat från din masterprofil tills du väljer att föra över den.
                </div>
                {!shared && doneCount > 0 && (
                  <div className="rounded-xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      Vill du föra över allt på en gång? {doneCount} av {totalSections} avsnitt har en sammanfattning.
                    </p>
                    <Button variant="outline" className="rounded-full shrink-0" onClick={transferAll} disabled={transferAllLoading} data-testid="interview-transfer-all-btn">
                      {transferAllLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Överför hela intervjun
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Button className="rounded-full" onClick={genSummary} disabled={genLoading} data-testid="interview-generate-btn">
                {genLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Skapa sammanfattning
              </Button>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button variant="ghost" className="rounded-full" onClick={back} data-testid="interview-summary-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Tillbaka
              </Button>
              {!isLastSection ? (
                <Button className="rounded-full" onClick={next} data-testid="interview-next-section-btn">
                  Nästa avsnitt <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Badge variant="secondary" className="rounded-full">Intervjun är genomförd</Badge>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
